/* The active resident roster, in one alphabet.
 *
 * Guest and former-resident rows still exist behind their profile URLs so every
 * archive credit keeps working. They are omitted here because this route is the
 * current roster, not an index of every artist who has appeared on the station.
 *
 * The A–Z bar is the navigation this page actually needs. Forty sleeves is two
 * screens of scrolling and a roster is the one list people arrive at with a
 * name already in mind. Letters nobody is filed under are drawn as plain text
 * with no rule around them — present and absent are different shapes, not
 * different shades.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import type { CSSProperties } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { api, isAbortError } from '../api';
import { Artwork, Chip, ChipList, Empty, ErrorNote, Section, Spinner, shortDate } from '../components';
import { readTzMode } from '../format';
import type { GenreOut, HostCard } from '../types';

const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

/** Where a name that does not start with a letter is filed. */
const OTHER = '#';

interface Filters {
  genre: string | null;
}

/**
 * The letter a name is filed under.
 *
 * Leading punctuation is stepped over; nothing else is. "The Vessels" is filed
 * under T and not V, because T is where the name starts and T is where somebody
 * scanning this bar will look for it. A name that begins with a digit or a
 * symbol goes to `#` rather than being dropped.
 */
function letterFor(name: string): string {
  const cleaned = name.replace(/^[^\p{L}\p{N}]+/u, '');
  const first = cleaned.charAt(0).toUpperCase();
  return first >= 'A' && first <= 'Z' ? first : OTHER;
}

function readFilters(params: URLSearchParams): Filters {
  const genre = (params.get('genre') ?? '').trim().toLowerCase();
  return {
    genre: genre || null,
  };
}

function toPath(filters: Filters): string {
  const params = new URLSearchParams();
  if (filters.genre) params.set('genre', filters.genre);
  const encoded = params.toString();
  return encoded ? `/residents?${encoded}` : '/residents';
}

/* ------------------------------------------------------------------ styles */

const HEAD: CSSProperties = {
  display: 'block',
  fontSize: 'clamp(2.75rem, 8vw, 4.75rem)',
  lineHeight: 0.92,
  letterSpacing: '-.015em',
  fontVariationSettings: "'wdth' 118, 'wght' 800",
};

const INTRO: CSSProperties = {
  maxWidth: '46ch',
  fontSize: '0.9375rem',
  color: 'var(--muted)',
};

const FILTERS: CSSProperties = {
  borderBottom: 'var(--bw) solid var(--edge)',
  background: 'var(--paper)',
};

const FROW: CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: '14px',
  padding: '14px clamp(12px, 4vw, 28px)',
  borderBottom: 'var(--bw-hair) solid var(--edge)',
  flexWrap: 'wrap',
};

const FLABEL: CSSProperties = {
  width: '96px',
  flexShrink: 0,
  color: 'var(--muted)',
  paddingTop: '9px',
};

const JUMPBAR: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: '4px',
  alignItems: 'center',
  padding: '12px clamp(12px, 4vw, 28px)',
  borderBottom: 'var(--bw) solid var(--edge)',
  background: 'var(--surface)',
};

const JUMP_ON: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: '44px',
  minHeight: '44px',
  padding: '0 8px',
  border: 'var(--bw-chip) solid var(--edge)',
  boxShadow: 'var(--sh-chip)',
  background: 'var(--paper)',
  color: 'var(--text)',
  textDecoration: 'none',
};

const JUMP_OFF: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: '44px',
  minHeight: '44px',
  padding: '0 8px',
  color: 'var(--muted)',
};

const GROUP_HEAD: CSSProperties = {
  display: 'flex',
  alignItems: 'baseline',
  gap: '16px',
  padding: '22px clamp(12px, 4vw, 28px) 10px',
  scrollMarginTop: 'calc(var(--nav-h) + 20px)',
};

const GROUP_LETTER: CSSProperties = {
  fontFamily: 'var(--ff-display)',
  fontSize: '2.25rem',
  lineHeight: 0.9,
  letterSpacing: '-.02em',
  textTransform: 'uppercase',
  margin: 0,
};

/* -------------------------------------------------------------------- page */

export default function Hosts() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [tz] = useState(readTzMode);

  const filters = useMemo(() => readFilters(searchParams), [searchParams]);

  const [hosts, setHosts] = useState<HostCard[] | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [attempt, setAttempt] = useState(0);

  const [genres, setGenres] = useState<GenreOut[] | null>(null);
  const [genresError, setGenresError] = useState<unknown>(null);
  const [genresAttempt, setGenresAttempt] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    setHosts(null);
    setError(null);

    api
      .getHosts(
        {
          resident: true,
          genre: filters.genre ?? undefined,
          status: 'active',
        },
        controller.signal,
      )
      .then(setHosts)
      .catch((failure: unknown) => {
        if (isAbortError(failure)) return;
        setError(failure);
      });

    return () => controller.abort();
  }, [filters, attempt]);

  useEffect(() => {
    const controller = new AbortController();
    setGenresError(null);
    api
      .getGenres(controller.signal)
      .then(setGenres)
      .catch((failure: unknown) => {
        if (isAbortError(failure)) return;
        setGenresError(failure);
      });
    return () => controller.abort();
  }, [genresAttempt]);

  useEffect(() => {
    document.title = 'Higher Ground Rhythm Police — Higher Ground Radio';
  }, []);

  const apply = useCallback(
    (next: Filters) => {
      navigate(toPath(next));
    },
    [navigate],
  );

  /* ---------------------------------------------------------- derivations */

  /** The roster, cut into letters, in alphabet order with `#` last. */
  const groups = useMemo(() => {
    if (!hosts) return [];
    const buckets = new Map<string, HostCard[]>();
    for (const host of hosts) {
      const letter = letterFor(host.name);
      const bucket = buckets.get(letter);
      if (bucket) bucket.push(host);
      else buckets.set(letter, [host]);
    }
    for (const bucket of buckets.values()) {
      bucket.sort((a, b) => a.name.localeCompare(b.name, 'en'));
    }
    return [...ALPHABET, OTHER]
      .filter((letter) => buckets.has(letter))
      .map((letter) => ({ letter, hosts: buckets.get(letter) ?? [] }));
  }, [hosts]);

  const filled = useMemo(() => new Set(groups.map((group) => group.letter)), [groups]);

  const visibleGenres = useMemo(() => {
    if (!genres) return [];
    return genres.filter((genre) => genre.episode_count > 0 || genre.slug === filters.genre);
  }, [genres, filters.genre]);

  const anyFilter = filters.genre !== null;

  /* ---------------------------------------------------------------- view */

  return (
    <>
      <Section
        level={1}
        title={<span style={HEAD}>Higher Ground Rhythm Police</span>}
        meta={
          hosts === null
            ? 'Loading'
            : `${hosts.length} active ${hosts.length === 1 ? 'member' : 'members'}`
        }
      >
        <p className="t-body" style={INTRO}>
          The resident crew holding Higher Ground Radio down. Guest artists remain credited on
          every set in the archive; this page belongs to the Rhythm Police.
        </p>
      </Section>

      <div style={FILTERS}>
        <div style={FROW}>
          <div className="mono" style={FLABEL}>
            Genre
          </div>
          {genresError ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap' }}>
              <span className="mono" role="alert">
                The genre list did not load
              </span>
              <button
                type="button"
                className="btn-ghost"
                onClick={() => setGenresAttempt((n) => n + 1)}
              >
                Try again
              </button>
            </div>
          ) : !genres ? (
            <Spinner label="Loading the genres" />
          ) : visibleGenres.length === 0 ? (
            <span className="mono dim">No genres have been broadcast yet.</span>
          ) : (
            <ChipList label="Filter by genre">
              {visibleGenres.map((genre) => {
                const selected = filters.genre === genre.slug;
                return (
                  <Chip
                    key={genre.slug}
                    color={selected ? 'sky' : 'paper'}
                    count={genre.episode_count}
                    selected={selected}
                    onPress={() => apply({ ...filters, genre: selected ? null : genre.slug })}
                  >
                    {genre.name}
                  </Chip>
                );
              })}
            </ChipList>
          )}
          {anyFilter ? (
            <Link
              className="mono"
              to="/residents"
              style={{
                marginLeft: 'auto',
                display: 'inline-flex',
                alignItems: 'center',
                minHeight: '44px',
              }}
            >
              Reset
            </Link>
          ) : null}
        </div>
      </div>

      {/* The jump bar. A letter with nobody under it is drawn without a rule
          and without a shadow, so present and absent are different SHAPES —
          telling them apart never depends on noticing a shade. */}
      <nav style={JUMPBAR} aria-label="Jump to a letter">
        <span className="mono dim" style={{ paddingRight: '10px' }}>
          Jump to
        </span>
        {[...ALPHABET, OTHER].map((letter) =>
          filled.has(letter) ? (
            <a className="mono" key={letter} href={`#letter-${letter === OTHER ? 'other' : letter}`} style={JUMP_ON}>
              {letter}
            </a>
          ) : (
            <span className="mono" key={letter} style={JUMP_OFF} aria-hidden="true">
              {letter}
            </span>
          ),
        )}
      </nav>

      {error ? (
        <ErrorNote error={error} what="the roster" onRetry={() => setAttempt((n) => n + 1)} />
      ) : hosts === null ? (
        <div style={{ padding: '34px clamp(12px, 4vw, 28px)' }}>
          <Spinner label="Loading the roster" />
        </div>
      ) : hosts.length === 0 ? (
        <Empty
          kind="Nobody listed"
          title={
            anyFilter
              ? 'Nobody on the roster matches those filters.'
              : 'There is nobody on the roster yet.'
          }
          actions={
            anyFilter ? (
              <>
                <Link className="btn" to="/residents">
                  Show everyone
                </Link>
                <Link className="btn-ghost" to="/archive">
                  Browse the archive
                </Link>
              </>
            ) : (
              <>
                <Link className="btn" to="/submit">
                  Send us a set
                </Link>
                <Link className="btn-ghost" to="/schedule">
                  What is coming up
                </Link>
              </>
            )
          }
        >
          <p className="t-body">
            {anyFilter
              ? 'Genre here means a genre somebody has actually broadcast, not one they say they play — so a narrow combination can honestly come back empty. Take a chip off.'
              : 'This is a new station. The first residency will show up on this page the day it is agreed.'}
          </p>
        </Empty>
      ) : (
        <>
          {groups.map((group) => (
            <section key={group.letter}>
              <div style={GROUP_HEAD} id={`letter-${group.letter === OTHER ? 'other' : group.letter}`}>
                <h2 style={GROUP_LETTER}>{group.letter}</h2>
                <span className="mono dim">
                  {group.hosts.length} {group.hosts.length === 1 ? 'name' : 'names'}
                </span>
              </div>

              <div className="cardgrid">
                {group.hosts.map((host) => {
                  return (
                    <Link
                      className="card"
                      key={host.slug}
                      to={`/residents/${encodeURIComponent(host.slug)}`}
                    >
                      {/* The one sleeve on the site that carries information
                          nothing beside it repeats: the monogram is computed on
                          the server so every surface agrees on "de Wit" = WI. */}
                      <Artwork
                        color={host.artwork_color}
                        imageUrl={host.artwork_url}
                        display={host.monogram}
                        label="Rhythm Police"
                        size="card"
                        decorative={false}
                      />
                      <div className="card__body">
                        <h3>{host.name}</h3>
                        <div className="mono dim">
                          {[host.city, host.resident_rank, 'Rhythm Police']
                            .filter(Boolean)
                            .join(' · ')}
                        </div>
                        <div className="mono">
                          {host.episode_count} {host.episode_count === 1 ? 'set' : 'sets'}
                          {host.latest
                            ? ` · latest ${shortDate(host.latest.broadcast_at, tz)}`
                            : ' · none yet'}
                        </div>

                        {host.latest ? (
                          <span
                            className="mono dim"
                            style={{
                              alignSelf: 'flex-start',
                              paddingLeft: '8px',
                              borderLeft: 'var(--bw-chip) solid var(--edge)',
                            }}
                          >
                            {host.latest.catalogue_no} {host.latest.title}
                          </span>
                        ) : null}
                      </div>
                    </Link>
                  );
                })}
              </div>
            </section>
          ))}
        </>
      )}
    </>
  );
}
